import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Select, Card, Row, Col, Upload, Space, Tag
} from 'antd';
import { PlusOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { getSkills, imagepath } from '../api';

const { Option } = Select;
const documentTypes = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'emirates_id', label: 'Emirates ID' },
  { value: 'labour_card', label: 'Labour Card' },
  { value: 'driving_licence', label: 'Driving Licence' },
  { value: 'health_insurance', label: 'Health Insurance' },
  { value: 'workmen_insurance', label: 'Workmen Insurance' },
];
const countryList = [
  'United Arab Emirates', 'India', 'Saudi Arabia', 'Qatar', 'Oman', 'Kuwait', 'Bahrain', 'Pakistan', 'Bangladesh', 'Nepal', 'Sri Lanka', 'Philippines', 'Other'
];

const EmployeeForm = ({ initialValues, onSubmit, loading, mode = 'add', departments = [], onCancel }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    if (initialValues) {
      // Transform documents data to match form structure
      const formattedValues = { ...initialValues };

      if (formattedValues.documents && formattedValues.documents.length > 0) {
        formattedValues.documents = formattedValues.documents.map(doc => ({
          ...doc,
          document_file: doc.document_file ? [{
            uid: doc.id,
            name: doc.original_filename || (typeof doc.document_file === 'string' ? doc.document_file.split('/').pop() : 'document'),
            status: 'done',
            url: doc.document_file.startsWith('http') ? doc.document_file : `${imagepath}${doc.document_file}`,
          }] : []
        }));
      }

      if (formattedValues.leaves && formattedValues.leaves.length > 0) {
        formattedValues.leaves = formattedValues.leaves.map(leave => ({
          ...leave,
          form_file: leave.leave_application_form ? [{
            uid: leave.id,
            name: leave.original_filename || leave.leave_application_form.split('/').pop(),
            status: 'done',
            url: `${imagepath}${leave.leave_application_form}`,
          }] : []
        }));
      }

      form.setFieldsValue(formattedValues);
      setImagePreview(
        formattedValues.profile_image && formattedValues.profile_image.length > 0 && formattedValues.profile_image[0].url
          ? formattedValues.profile_image[0].url
          : null
      );
    }
    // eslint-disable-next-line
  }, [initialValues]);

  useEffect(() => {
    getSkills().then(res => {
      if (res.success) setSkills(res.data);
    });
  }, []);

  const onFinish = async (values) => {
    console.log('EmployeeForm onFinish - Raw values:', values);
    console.log('Documents in values:', values.documents);

    // Process documents to ensure they have the correct structure
    const processedValues = {
      ...values,
      documents: values.documents?.map(doc => {
        console.log('Processing document:', doc);
        return {
          ...doc,
          // Ensure document_file is an array with the file object
          document_file: Array.isArray(doc.document_file) ? doc.document_file : [doc.document_file]
        };
      }) || []
    };

    console.log('Processed values:', processedValues);
    console.log('Final documents:', processedValues.documents);

    if (onSubmit) {
      onSubmit(processedValues);
    }
  };

  // Handle initial document fields
  const [initialized, setInitialized] = useState(false);
  const formDocuments = Form.useWatch('documents', form) || [];

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', padding: '40px 5vw', borderRadius: 0 }}>
      <h2>{mode === 'edit' ? 'Edit Employee' : 'Add Employee'}</h2>
      <div style={{ marginBottom: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button type="primary" htmlType="submit" form={form.getForm ? form.getForm().id : undefined} loading={loading} onClick={() => form.submit()}>
          {mode === 'edit' ? 'Update Employee' : 'Create Employee'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          documents: initialValues?.documents?.map(doc => ({
            ...doc,
            // Ensure document_file is always an array
            document_file: Array.isArray(doc.document_file) ? doc.document_file : [doc.document_file]
          })) || [],
          leaves: initialValues?.leaves || []
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Personal Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="emp_code" label="Employee Code">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Invalid email' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="phone" label="Phone (UAE)">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="native_phone" label="Native Phone (Home Country)">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dob" label="Date of Birth">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="address" label="Address">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="country" label="Country">
                    <Select showSearch optionFilterProp="children" placeholder="Select country">
                      {countryList.map(c => <Option key={c} value={c}>{c}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="profile_image"
                    label="Profile Image"
                    valuePropName="fileList"
                    getValueFromEvent={e => {
                      if (Array.isArray(e)) return e.slice(-1);
                      return e && e.fileList ? e.fileList.slice(-1) : [];
                    }}
                  >
                    <Upload
                      showUploadList={false}
                      maxCount={1}
                      beforeUpload={() => false}
                      onChange={info => {
                        const fileList = info.fileList.slice(-1);
                        if (fileList.length > 0) {
                          const file = fileList[0];
                          if (file.originFileObj) {
                            setImagePreview(URL.createObjectURL(file.originFileObj));
                          } else if (file.url) {
                            setImagePreview(file.url);
                          }
                        } else {
                          setImagePreview(null);
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />}>{imagePreview ? 'Change Image' : 'Select Image'}</Button>
                    </Upload>
                  </Form.Item>
                  {imagePreview && (
                    <div style={{ marginBottom: 16 }}>
                      <img src={imagePreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Job Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="department_id" label="Company" rules={[{ required: true, message: 'Please select company' }]}>
                    <Select allowClear placeholder="Select company">
                      {departments.map(dep => <Option key={dep.id} value={dep.id}>{dep.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="skills" label="Skills">
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select skill(s)"
                      optionFilterProp="children"
                    >
                      {skills.map(skill => (
                        <Option key={skill.id} value={skill.id}>{skill.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select status' }]}>
                    <Select>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="joining_date" label="Joining Date">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workmen_insurance_issue_date" label="Workmen Insurance Issue Date">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workmen_insurance_expiry_date" label="Workmen Insurance Expiry Date">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="salary_type" label="Salary Type">
                    <Select>
                      <Option value="monthly">Monthly</Option>
                      <Option value="hourly">Hourly</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.salary_type !== currentValues.salary_type}
                >
                  {({ getFieldValue }) => {
                    const salaryType = getFieldValue('salary_type');
                    if (salaryType === 'monthly') {
                      return (
                        <>
                          <Col span={12}>
                            <Form.Item name="base_salary" label="Base Salary">
                              <Input type="number" />
                            </Form.Item>
                          </Col>
                        </>
                      );
                    }
                    if (salaryType === 'hourly') {
                      return (
                        <Col span={12}>
                          <Form.Item name="hourly_rate" label="Hourly Rate">
                            <Input type="number" />
                          </Form.Item>
                        </Col>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
                <Col span={12}>
                  <Form.Item name="over_time_rate" label="Overtime Rate">
                    <Input type="number" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        <Card title="Leave History" style={{ marginBottom: 24 }}>
          <Form.List name="leaves">
            {(fields, { add, remove }) => (
              <div key="leaves-container">
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8, flexWrap: 'wrap' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'leave_type']}
                      label="Type"
                      rules={[{ required: true, message: 'Type' }]}
                    >
                      <Select placeholder="Type" style={{ width: 140 }}>
                        <Option value="Annual">Annual</Option>
                        <Option value="Sick">Sick</Option>
                        <Option value="Unpaid">Unpaid</Option>
                        <Option value="Emergency">Emergency</Option>
                        <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'start_date']}
                      label="From"
                      rules={[{ required: true, message: 'Start' }]}
                    >
                      <Input type="date" placeholder="Start Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'end_date']}
                      label="To"
                      rules={[{ required: true, message: 'End' }]}
                    >
                      <Input type="date" placeholder="End Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'actual_return_date']}
                      label="Actual Return"
                    >
                      <Input type="date" placeholder="Actual Return Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => {
                        const prevLeave = prevValues.leaves?.[name];
                        const currLeave = currentValues.leaves?.[name];
                        return prevLeave?.end_date !== currLeave?.end_date || prevLeave?.actual_return_date !== currLeave?.actual_return_date;
                      }}
                    >
                      {({ getFieldValue }) => {
                        const leave = getFieldValue(['leaves', name]);
                        if (leave?.end_date && leave?.actual_return_date) {
                          const end = new Date(leave.end_date);
                          const actual = new Date(leave.actual_return_date);

                          // Expected return is end_date + 1 day
                          const expected = new Date(end);
                          expected.setDate(expected.getDate() + 1);

                          // Reset hours to compare dates correctly
                          expected.setHours(0, 0, 0, 0);
                          actual.setHours(0, 0, 0, 0);

                          const diffTime = actual - expected;
                          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

                          if (diffDays > 0) {
                            return <Tag color="red" style={{ marginTop: 30 }}>{diffDays} Day(s) Late</Tag>;
                          } else if (diffDays < 0) {
                            return <Tag color="green" style={{ marginTop: 30 }}>{Math.abs(diffDays)} Day(s) Early</Tag>;
                          } else {
                            return <Tag color="blue" style={{ marginTop: 30 }}>On Time</Tag>;
                          }
                        }
                        return null;
                      }}
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'reason']}
                      label="Reason"
                    >
                      <Input placeholder="Reason/Remarks" style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'form_file']}
                      label="Form"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e.slice(-1);
                        return e && e.fileList ? e.fileList.slice(-1) : [];
                      }}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        listType="picture"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      >
                        <Button icon={<UploadOutlined />} size="small">File</Button>
                      </Upload>
                    </Form.Item>
                    <div style={{ marginTop: 30 }}>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        title="Remove Leave Record"
                      />
                    </div>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Leave Record
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>

        <Card title="Employee Documents" style={{ marginBottom: 24 }}>
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <div key="documents-container">
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'document_type']}
                      rules={[{ required: true, message: 'Type' }]}
                    >
                      <Select placeholder="Type" style={{ width: 140 }}>
                        {documentTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'issue_date']}
                      label="Issue Date"
                      getValueProps={(value) => ({
                        value: value ? new Date(value).toISOString().split('T')[0] : ''
                      })}
                    >
                      <Input type="date" placeholder="Issue Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'expiry_date']}
                      label="Expiry Date"
                      rules={[{ required: true, message: 'Expiry date is required' }]}
                      getValueProps={(value) => ({
                        value: value ? new Date(value).toISOString().split('T')[0] : ''
                      })}
                    >
                      <Input type="date" placeholder="Expiry Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'document_file']}
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e.slice(-1);
                        return e && e.fileList ? e.fileList.slice(-1) : [];
                      }}
                      rules={[
                        {
                          required: mode === 'add',
                          message: 'Document file is required'
                        },
                        {
                          validator: (_, fileList) => {
                            if (!fileList || fileList.length === 0) return Promise.resolve();
                            const file = fileList[0];
                            // Only validate size for new uploads
                            if (file.originFileObj) {
                              const isLt2M = file.size / 1024 / 1024 < 2;
                              if (!isLt2M) {
                                return Promise.reject('File must be smaller than 2MB');
                              }
                              const isAcceptableType = [
                                'image/jpeg',
                                'image/png',
                                'application/pdf',
                                'application/msword',
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                              ].includes(file.type);
                              if (!isAcceptableType) {
                                return Promise.reject('File type must be JPG, PNG, PDF, or DOC/DOCX');
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        listType="picture"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      >
                        <Button icon={<UploadOutlined />}>Choose File</Button>
                      </Upload>
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Document
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>
      </Form>
    </div>
  );
};

export default EmployeeForm; 